import React from 'react'
    import { Heart, DollarSign, Building2, Shield, Users, TrendingUp } from 'lucide-react'

    const Industries = () => {
      const industries = [
        {
          title: "Healthcare & Life Sciences",
          icon: <Heart className="h-12 w-12 text-red-500" />,
          image: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=400&fit=crop",
          description: "Transforming healthcare through AI-powered automation and intelligent systems.",
          solutions: [
            "Prior Authorization Automation",
            "Claims Processing & Adjudication", 
            "Member Engagement Systems",
            "Clinical Decision Support",
            "Supply Chain Optimization",
            "Regulatory Compliance Monitoring"
          ],
          benefits: [
            "Reduce processing time by 80%",
            "Improve patient satisfaction",
            "Ensure HIPAA compliance",
            "Lower operational costs"
          ]
        },
        {
          title: "Financial Services & Compliance",
          icon: <DollarSign className="h-12 w-12 text-green-500" />,
          image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
          description: "Combat financial crime with autonomous AI systems and intelligent compliance monitoring.",
          solutions: [
            "Real-time Transaction Monitoring",
            "Intelligent Watchlist Screening",
            "Enhanced Due Diligence (EDD)",
            "Anti-Money Laundering (AML)",
            "Know Your Customer (KYC)",
            "Regulatory Reporting Automation"
          ],
          benefits: [
            "99.9% accuracy in fraud detection",
            "Reduce false positives by 70%",
            "Automated compliance reporting",
            "Global regulatory adherence"
          ]
        },
        {
          title: "Enterprise & SaaS Solutions",
          icon: <Building2 className="h-12 w-12 text-blue-500" />,
          image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop",
          description: "Embed agentic capabilities into your software products and enterprise systems.",
          solutions: [
            "Intelligent Process Automation",
            "Customer Service AI Agents",
            "Predictive Analytics Integration",
            "Smart Content Management",
            "Automated Quality Assurance",
            "Dynamic Workflow Optimization"
          ],
          benefits: [
            "Increase productivity by 60%",
            "Reduce manual errors",
            "Improve customer experience",
            "Scale operations efficiently"
          ]
        }
      ]

      const platforms = [
        { name: "Actimize", category: "Financial Crime" },
        { name: "Fenergo", category: "Client Lifecycle" },
        { name: "OFSAA", category: "Risk Management" },
        { name: "Salesforce", category: "CRM Platform" },
        { name: "ServiceNow", category: "IT Service Management" },
        { name: "Microsoft Dynamics", category: "ERP Solutions" }
      ]

      const regulations = [
        { name: "SEC", region: "United States" },
        { name: "FATF", region: "Global" },
        { name: "FinCEN", region: "United States" },
        { name: "BaFin", region: "Germany" },
        { name: "goAML", region: "Global" },
        { name: "MiFID II", region: "European Union" }
      ]

      return (
        <div className="min-h-screen bg-white" data-component-path="src/pages/Industries.jsx" data-component-line="1:4" data-component-id="src/pages/Industries.jsx:1:4">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20" data-component-path="src/pages/Industries.jsx" data-component-line="3:6" data-component-id="src/pages/Industries.jsx:3:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Industries.jsx" data-component-line="4:8" data-component-id="src/pages/Industries.jsx:4:8">
              <div className="text-center" data-component-path="src/pages/Industries.jsx" data-component-line="5:10" data-component-id="src/pages/Industries.jsx:5:10">
                <h1 className="text-5xl font-bold text-gray-900 mb-6" data-component-path="src/pages/Industries.jsx" data-component-line="6:12" data-component-id="src/pages/Industries.jsx:6:12">
                  Industries We <span className="gradient-text" data-component-path="src/pages/Industries.jsx" data-component-line="7:14" data-component-id="src/pages/Industries.jsx:7:14">Serve</span>
                </h1>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-component-path="src/pages/Industries.jsx" data-component-line="9:12" data-component-id="src/pages/Industries.jsx:9:12">
                  Tailored intelligence for demanding sectors with deep domain expertise and proven results.
                </p>
              </div>
            </div>
          </section>

          {/* Industries Detail */}
          <section className="py-20" data-component-path="src/pages/Industries.jsx" data-component-line="16:6" data-component-id="src/pages/Industries.jsx:16:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Industries.jsx" data-component-line="17:8" data-component-id="src/pages/Industries.jsx:17:8">
              <div className="space-y-20" data-component-path="src/pages/Industries.jsx" data-component-line="18:10" data-component-id="src/pages/Industries.jsx:18:10">
                {industries.map((industry, index) => (
                  <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`} data-component-path="src/pages/Industries.jsx" data-component-line="19:12" data-component-id="src/pages/Industries.jsx:19:12">
                    <div className={index % 2 === 1 ? 'lg:col-start-2' : ''} data-component-path="src/pages/Industries.jsx" data-component-line="20:14" data-component-id="src/pages/Industries.jsx:20:14">
                      <div className="flex items-center space-x-4 mb-6" data-component-path="src/pages/Industries.jsx" data-component-line="21:16" data-component-id="src/pages/Industries.jsx:21:16">
                        {industry.icon}
                        <h2 className="text-3xl font-bold text-gray-900" data-component-path="src/pages/Industries.jsx" data-component-line="23:16" data-component-id="src/pages/Industries.jsx:23:16">{industry.title}</h2>
                      </div>
                      <p className="text-lg text-gray-600 mb-8" data-component-path="src/pages/Industries.jsx" data-component-line="25:16" data-component-id="src/pages/Industries.jsx:25:16">{industry.description}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-component-path="src/pages/Industries.jsx" data-component-line="27:16" data-component-id="src/pages/Industries.jsx:27:16">
                        <div data-component-path="src/pages/Industries.jsx" data-component-line="28:18" data-component-id="src/pages/Industries.jsx:28:18">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-component-path="src/pages/Industries.jsx" data-component-line="29:20" data-component-id="src/pages/Industries.jsx:29:20">Our Solutions</h3>
                          <ul className="space-y-2" data-component-path="src/pages/Industries.jsx" data-component-line="30:20" data-component-id="src/pages/Industries.jsx:30:20">
                            {industry.solutions.map((solution, sIndex) => (
                              <li key={sIndex} className="flex items-center space-x-2" data-component-path="src/pages/Industries.jsx" data-component-line="31:22" data-component-id="src/pages/Industries.jsx:31:22">
                                <div className="w-2 h-2 bg-blue-600 rounded-full" data-component-path="src/pages/Industries.jsx" data-component-line="32:24" data-component-id="src/pages/Industries.jsx:32:24"></div>
                                <span className="text-gray-700" data-component-path="src/pages/Industries.jsx" data-component-line="33:24" data-component-id="src/pages/Industries.jsx:33:24">{solution}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div data-component-path="src/pages/Industries.jsx" data-component-line="38:18" data-component-id="src/pages/Industries.jsx:38:18">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-component-path="src/pages/Industries.jsx" data-component-line="39:20" data-component-id="src/pages/Industries.jsx:39:20">Key Benefits</h3>
                          <ul className="space-y-2" data-component-path="src/pages/Industries.jsx" data-component-line="40:20" data-component-id="src/pages/Industries.jsx:40:20">
                            {industry.benefits.map((benefit, bIndex) => (
                              <li key={bIndex} className="flex items-center space-x-2" data-component-path="src/pages/Industries.jsx" data-component-line="41:22" data-component-id="src/pages/Industries.jsx:41:22">
                                <TrendingUp className="w-4 h-4 text-green-600" data-component-path="src/pages/Industries.jsx" data-component-line="42:24" data-component-id="src/pages/Industries.jsx:42:24" />
                                <span className="text-gray-700" data-component-path="src/pages/Industries.jsx" data-component-line="43:24" data-component-id="src/pages/Industries.jsx:43:24">{benefit}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    
                    <div className={index % 2 === 1 ? 'lg:col-start-1' : ''} data-component-path="src/pages/Industries.jsx" data-component-line="49:14" data-component-id="src/pages/Industries.jsx:49:14">
                      <img 
                        src={industry.image} 
                        alt={industry.title}
                        className="w-full rounded-xl shadow-lg"
                        loading="lazy"
                        data-component-path="src/pages/Industries.jsx" data-component-line="50:16" data-component-id="src/pages/Industries.jsx:50:16"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Platforms & Regulations */}
          <section className="py-20 bg-gray-50" data-component-path="src/pages/Industries.jsx" data-component-line="62:6" data-component-id="src/pages/Industries.jsx:62:6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="src/pages/Industries.jsx" data-component-line="63:8" data-component-id="src/pages/Industries.jsx:63:8">
              <div className="text-center mb-16" data-component-path="src/pages/Industries.jsx" data-component-line="64:10" data-component-id="src/pages/Industries.jsx:64:10">
                <h2 className="text-3xl font-bold text-gray-900 mb-4" data-component-path="src/pages/Industries.jsx" data-component-line="65:12" data-component-id="src/pages/Industries.jsx:65:12">
                  Platforms & Regulatory Expertise
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-component-path="src/pages/Industries.jsx" data-component-line="68:12" data-component-id="src/pages/Industries.jsx:68:12">
                  Deep expertise in industry-leading platforms and global regulatory frameworks.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-component-path="src/pages/Industries.jsx" data-component-line="73:10" data-component-id="src/pages/Industries.jsx:73:10">
                {/* Platforms */}
                <div className="bg-white rounded-xl p-8 shadow-lg" data-component-path="src/pages/Industries.jsx" data-component-line="75:12" data-component-id="src/pages/Industries.jsx:75:12">
                  <div className="flex items-center space-x-3 mb-6" data-component-path="src/pages/Industries.jsx" data-component-line="76:14" data-component-id="src/pages/Industries.jsx:76:14">
                    <Building2 className="h-8 w-8 text-blue-600" data-component-path="src/pages/Industries.jsx" data-component-line="77:16" data-component-id="src/pages/Industries.jsx:77:16" />
                    <h3 className="text-2xl font-bold text-gray-900" data-component-path="src/pages/Industries.jsx" data-component-line="78:16" data-component-id="src/pages/Industries.jsx:78:16">Platform Expertise</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-component-path="src/pages/Industries.jsx" data-component-line="80:14" data-component-id="src/pages/Industries.jsx:80:14">
                    {platforms.map((platform, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg" data-component-path="src/pages/Industries.jsx" data-component-line="81:16" data-component-id="src/pages/Industries.jsx:81:16">
                        <div className="font-semibold text-gray-900" data-component-path="src/pages/Industries.jsx" data-component-line="82:18" data-component-id="src/pages/Industries.jsx:82:18">{platform.name}</div>
                        <div className="text-sm text-gray-600" data-component-path="src/pages/Industries.jsx" data-component-line="83:18" data-component-id="src/pages/Industries.jsx:83:18">{platform.category}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Regulations */}
                <div className="bg-white rounded-xl p-8 shadow-lg" data-component-path="src/pages/Industries.jsx" data-component-line="89:12" data-component-id="src/pages/Industries.jsx:89:12">
                  <div className="flex items-center space-x-3 mb-6" data-component-path="src/pages/Industries.jsx" data-component-line="90:14" data-component-id="src/pages/Industries.jsx:90:14">
                    <Shield className="h-8 w-8 text-green-600" data-component-path="src/pages/Industries.jsx" data-component-line="91:16" data-component-id="src/pages/Industries.jsx:91:16" />
                    <h3 className="text-2xl font-bold text-gray-900" data-component-path="src/pages/Industries.jsx" data-component-line="92:16" data-component-id="src/pages/Industries.jsx:92:16">Regulatory Compliance</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-component-path="src/pages/Industries.jsx" data-component-line="94:14" data-component-id="src/pages/Industries.jsx:94:14">
                    {regulations.map((regulation, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg" data-component-path="src/pages/Industries.jsx" data-component-line="95:16" data-component-id="src/pages/Industries.jsx:95:16">
                        <div className="font-semibold text-gray-900" data-component-path="src/pages/Industries.jsx" data-component-line="96:18" data-component-id="src/pages/Industries.jsx:96:18">{regulation.name}</div>
                        <div className="text-sm text-gray-600" data-component-path="src/pages/Industries.jsx" data-component-line="97:18" data-component-id="src/pages/Industries.jsx:97:18">{regulation.region}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }

    export default Industries